import {
  BatchSdgTargetReasoningsRequest,
  BatchSdgTargetReasoningsResponse,
  SdgTargetReasoningKey,
} from '@upright/common/model/output/scores/sdg-alignment/sdg-target-reasoning-api-types';
import { useIsBrowserTabInFocus } from '@upright/ui/common/generic/hooks/use-is-browser-tab-in-focus';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

// Re-POST every ~3s while reasonings are still being generated, for up to ~60s.
const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 60_000;

export async function fetchSdgReasonings(items: SdgTargetReasoningKey[]): Promise<BatchSdgTargetReasoningsResponse> {
  const payload: BatchSdgTargetReasoningsRequest = { items };
  return await fetchOrThrow<BatchSdgTargetReasoningsResponse>('/sdg-reasonings', 'POST', payload);
}

/**
 * Fetches generated reasonings for a batch of (productCode, sdgTarget, direction)
 * triples surfaced on an SDG lens page.
 *
 * The full set is re-POSTed every ~3s while the response still reports `pending`
 * items, for up to ~60s. Polling pauses while the browser tab is hidden (we use
 * `enabled` keyed on tab focus so react-query stops scheduling refetches) and
 * resumes when it becomes visible again.
 *
 * Returns the query data plus `generationStopped`: `true` once no more reasonings
 * will arrive (the request errored, the ~60s window elapsed, or the batch came
 * back with no `pending` left). Callers use this to tell "still generating"
 * (show a loading hint) from "gave up" (show nothing) per (product, target,
 * direction) key.
 */
export function useSdgReasonings(items: SdgTargetReasoningKey[]): {
  data: BatchSdgTargetReasoningsResponse | undefined;
  isError: boolean;
  generationStopped: boolean;
} {
  const isTabInFocus = useIsBrowserTabInFocus();

  // Wall-clock start of the polling window. Reset whenever the set of items
  // changes (the query key changes), so each distinct batch gets its own ~60s.
  const startedAtRef = useRef<number>(Date.now());
  const itemsKey = buildItemsKey(items);
  const previousItemsKeyRef = useRef(itemsKey);
  if (previousItemsKeyRef.current !== itemsKey) {
    previousItemsKeyRef.current = itemsKey;
    startedAtRef.current = Date.now();
  }

  // Mirror of the `refetchInterval` timeout as React state, so the UI actually
  // re-renders at the ~60s mark and flips still-pending keys from "loading" to
  // "failed". `refetchInterval` returning false stops polling but does not
  // trigger a render on its own, so without this a stuck-pending key would show
  // "loading" forever. Re-armed whenever the batch (itemsKey) changes.
  const [hasTimedOut, setHasTimedOut] = useState(false);
  useEffect(() => {
    setHasTimedOut(false);
    const timer = setTimeout(() => {
      setHasTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [itemsKey]);

  const query = useQuery({
    queryKey: ['sdgReasonings', itemsKey],
    queryFn: async () => await fetchSdgReasonings(items),
    enabled: items.length > 0 && isTabInFocus,
    staleTime: 120 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: liveQuery => {
      const data = liveQuery.state.data;
      const stillPending = !data || data.pending.length > 0;
      const timedOut = Date.now() - startedAtRef.current > POLL_TIMEOUT_MS;
      if (!stillPending || timedOut) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });

  const generationStopped =
    query.isError || hasTimedOut || (query.data !== undefined && query.data.pending.length === 0);

  return { data: query.data, isError: query.isError, generationStopped };
}

function buildItemsKey(items: SdgTargetReasoningKey[]): string {
  return items
    .map(item => `${item.productCode}|${item.sdgTarget}|${item.direction}`)
    .sort()
    .join(',');
}
